import {
  hasAnyPermission,
  hasPermission,
  type RoleLike,
} from '@construction/shared';
import { ROUTES, ROUTE_BY_ID } from './registry';
import type { Entry, Gate, MobileTabId, RouteDef, RouteId, Shell, Slot, Variant } from './types';

/**
 * 读路由表的函数（CR-040）。React 组件和 `registry.test.ts` 用的是同一份 ——
 * 测试穷举过的规则就是界面上跑的规则。不引 React、不引 store。
 */

/** 每个入口位置属于哪套外壳 */
export const SLOT_SHELL: Record<Slot, Shell> = {
  siteNav: 'desktop',
  globalNav: 'desktop',
  account: 'desktop',
  tab: 'mobile',
  homeCard: 'mobile',
  meMenu: 'mobile',
  systemHub: 'mobile',
};

export function gatePass(role: RoleLike | null | undefined, gate: Gate): boolean {
  if (!role) return false;
  if (gate.system) return role.isSystem;
  if (gate.notSystem && role.isSystem) return false;
  if (gate.anyOf && gate.anyOf.length > 0 && !hasAnyPermission(role, gate.anyOf)) return false;
  if (gate.allOf && !gate.allOf.every((key) => hasPermission(role, key))) return false;
  if (gate.noneOf && gate.noneOf.some((key) => hasPermission(role, key))) return false;
  return true;
}

/** 第一个过门槛的变体；一个都过不了 = 这个人进不了这一页 */
export function resolveVariant(route: RouteDef, role: RoleLike | null | undefined): Variant | null {
  return route.variants.find((variant) => gatePass(role, variant.gate)) ?? null;
}

export function routeOpen(route: RouteDef, role: RoleLike | null | undefined): boolean {
  return resolveVariant(route, role) !== null;
}

export function inShell(route: RouteDef, shell: Shell): boolean {
  return route.shell === shell || route.shell === 'both';
}

export function routesForShell(shell: Shell): RouteDef[] {
  return ROUTES.filter((route) => inShell(route, shell));
}

export interface VisibleEntry {
  route: RouteDef;
  entry: Entry;
}

/** 某个位置上这个人看得到的入口，按 order 排 */
export function visibleEntries(role: RoleLike | null | undefined, slot: Slot): VisibleEntry[] {
  const shell = SLOT_SHELL[slot];
  const out: VisibleEntry[] = [];
  for (const route of ROUTES) {
    if (!inShell(route, shell)) continue;
    for (const entry of route.entries) {
      if (entry.slot !== slot) continue;
      if (!routeOpen(route, role)) continue;
      if (entry.gate && !gatePass(role, entry.gate)) continue;
      out.push({ route, entry });
    }
  }
  return out.sort((a, b) => a.entry.order - b.entry.order);
}

/**
 * 底部栏「首页」和「我的」之外最多几格。理由见 MobileLayout.tsx 顶上（CR-029）。
 * CR-044 起候选正好三个（日报、隐蔽工程、采购），名额不再有人抢；上限留着挡以后再加格子。
 */
export const MOBILE_MIDDLE_TAB_LIMIT = 3;

export interface MobileTabs {
  middle: Array<{ id: MobileTabId; route: RouteDef }>;
}

/**
 * 底部栏：首页 + 中间最多三格 + 我的（CR-044）。
 * 中间位置固定：日报 → 隐蔽工程 → 采购，有权限才出现（客户原话「位置固定，有权限才出现」）。
 * 待办、打卡、工地概览、图纸、进度表、考勤都在首页卡片上。
 *
 * **第一格对所有人都叫「首页」**（CR-044，客户原话「我需要统一首页都是/m ，app里任何人都是看见首页」）。
 * 改之前按权限叫「工地概览」或「首页」，其实是同一页 —— 网页端的工地概览是另一页，现在是首页上的一张卡。
 */
export function pickMobileTabs(role: RoleLike | null | undefined): MobileTabs {
  const middle = visibleEntries(role, 'tab')
    .filter((v) => v.entry.tab)
    .slice(0, MOBILE_MIDDLE_TAB_LIMIT)
    .map((v) => ({ id: v.entry.tab!, route: v.route }));
  return { middle };
}

export function pathFor(route: RouteDef, siteId: number | null): string | null {
  if (!route.siteScoped) return route.path;
  if (siteId === null) return null;
  return route.path.replace(':siteId', String(siteId));
}

/**
 * 两套外壳共用的页面里，「点进去去哪」按当前外壳取（CR-044）：
 * 给的路由本身在这套外壳里就用它，不在就用它的 `twin`。两个都不在返回 null（这一格就不是链接）。
 * 工地概览的格子按网页端路由写，App 里自动换成 `/m/...` —— 页面里不用写死任何一边。
 */
export function shellRoute(id: RouteId, shell: Shell): RouteDef | null {
  const route = ROUTE_BY_ID.get(id);
  if (!route) return null;
  if (inShell(route, shell)) return route;
  const twin = route.twin ? ROUTE_BY_ID.get(route.twin) : undefined;
  return twin && inShell(twin, shell) ? twin : null;
}

/**
 * 进了某个工地该落在哪：能进、`landing` 最小的那一页。
 * 一个都没有返回 null（这个人在工地里没有能看的页面，只剩工地之外的）。
 * （改之前还要排除「去 App 做」的提示页；网页端能打卡之后（CR-069）没有提示页了）
 *
 * **已归档的工地例外**（[CR-079]）：`landing` 最小的是工地概览，而概览上全是「今天」的数字
 * （在岗、待复核、今日日报），归档的工地上永远没有今天 —— 客户点进去看到的是一页空白。
 * 改设置的人直接送去工地设置：那里有地址、围栏、成员和「恢复工地」。
 * 没有 `site.manage` 的人照旧按能进的第一页落 —— 日报、隐蔽工程这些历史记录还看得到。
 */
export function siteLanding(
  role: RoleLike | null | undefined,
  siteId: number,
  opts?: { archived?: boolean },
): string | null {
  if (opts?.archived) {
    const settings = ROUTE_BY_ID.get('d.siteSettings');
    if (settings && routeOpen(settings, role)) return pathFor(settings, siteId);
  }
  const best = ROUTES.filter(
    (route) =>
      route.shell === 'desktop' &&
      route.siteScoped &&
      route.landing !== undefined &&
      resolveVariant(route, role)?.page !== undefined,
  ).sort((a, b) => a.landing! - b.landing!)[0];
  return best ? pathFor(best, siteId) : null;
}

/**
 * 这套外壳的落脚点。手机端永远是首页；网页端先进当前工地里第一个能看的页面，
 * 工地里一页都看不了就去工地之外第一个能进的（工地列表永远能进）。
 */
export function landingPath(
  role: RoleLike | null | undefined,
  shell: Shell,
  siteId: number | null,
): string {
  if (shell === 'mobile') return '/m';
  if (siteId !== null) {
    const inSite = siteLanding(role, siteId);
    if (inSite) return inSite;
  }
  const global = ROUTES.filter(
    (route) =>
      route.shell === 'desktop' && !route.siteScoped && route.landing !== undefined && routeOpen(route, role),
  ).sort((a, b) => a.landing! - b.landing!)[0];
  return global?.path ?? '/sites';
}

const MATCHERS = ROUTES.map((route) => ({
  route,
  re: new RegExp(
    `^${route.path
      .split('/')
      .map((seg) => (seg === ':siteId' ? '(\\d+)' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      .join('/')}/?$`,
  ),
}));

export function matchRoute(pathname: string): { route: RouteDef; siteId: number | null } | null {
  for (const { route, re } of MATCHERS) {
    const m = re.exec(pathname);
    if (m) return { route, siteId: m[1] !== undefined ? Number(m[1]) : null };
  }
  return null;
}

/**
 * App 里这一页的返回键去哪（CR-044）：路由表里写的固定上一级，没写就是底部栏里的页面、不画返回键。
 * 不按浏览历史走 —— 拍照后 Activity 被回收重开时历史是空的（CR-037）。
 */
export function backTarget(pathname: string): RouteDef | null {
  const matched = matchRoute(pathname);
  const parent = matched?.route.parent;
  return parent ? (ROUTE_BY_ID.get(parent) ?? null) : null;
}

/**
 * 换了外壳（拖窄窗口、旧链接）时，去另一套外壳里对应的那一页。
 * 对应页进不了、或者缺工地 id，就去那套外壳的落脚点。查询串原样带上
 * （拍照被打断后恢复用的 `?compose=1` 靠它）。
 */
export function twinPath(
  role: RoleLike | null | undefined,
  pathname: string,
  search: string,
  target: Shell,
  fallbackSiteId: number | null,
): string {
  const matched = matchRoute(pathname);
  const twin: RouteDef | undefined = matched?.route.twin
    ? ROUTE_BY_ID.get(matched.route.twin as RouteId)
    : undefined;
  if (twin && inShell(twin, target) && routeOpen(twin, role)) {
    const path = pathFor(twin, matched?.siteId ?? fallbackSiteId);
    if (path) return path + search;
  }
  return target === 'mobile' ? '/m' : '/';
}
